import { DOCUMENT, inject, Injectable, OnDestroy, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { selectAuthCelloJwt } from '@app/reducers/auth/auth.selectors';
import { EmployeeModel } from '@app/reducers/orm/employee/employee.model';
import { getAuthenticatedUser } from '@app/reducers/orm/employee/employee.selectors';
import { WindowRef } from '@app/shared/fixed/window';
import { Store } from '@ngrx/store';
import { AppState } from '@reducers/interfaces';
import { CookieService } from 'ngx-cookie-service';
import { environment } from 'src/environments/environment';

import { CustomerIOService } from './customer-io.service';

const MAX_RETRIES = 25;
const RETRY_DELAY_MS = 200;

export interface CelloAttributionPayload {
  ucc: string | null;
  referrerName: string | null;
  campaignConfig: any | null;
}

// Possible values for 'cello-open' query parameter
export const CELLO_QUERY_PARAM_VALUES = ['true', 'invite', 'rewards', 'edit-payments'];

@Injectable({ providedIn: 'root' })
export class CelloService implements OnDestroy {
  private document = inject(DOCUMENT);
  private window = inject(WindowRef).nativeWindow;
  private store = inject(Store<AppState>);
  private customerIoService = inject(CustomerIOService);
  private route = inject(ActivatedRoute);
  private cookieService = inject(CookieService);
  private initializingPromise: Promise<any> | null = null;

  private authenticatedUser: Signal<EmployeeModel> = this.store.selectSignal(getAuthenticatedUser);
  private celloJwt: Signal<string | null> = this.store.selectSignal(selectAuthCelloJwt);
  private scriptLoaded = false;
  private initialized = false;
  private bootFailed = false;
  private bootRetries = 0;
  private bootInterval: ReturnType<typeof setTimeout> | null = null;
  private productId = environment.celloProductId;

  private attributionInterval: ReturnType<typeof setTimeout> | null = null;
  private initializingAttributionPromise: Promise<void> | null = null;
  private attributionPromise: Promise<CelloAttributionPayload> | null = null;
  private attributionScriptLoaded = false;

  ngOnDestroy() {
    if (this.bootInterval) {
      clearInterval(this.bootInterval);
    }
    if (this.attributionInterval) {
      clearInterval(this.attributionInterval);
    }
  }

  async initialize() {
    if (this.initialized) {
      return Promise.resolve(true);
    }

    if (this.bootFailed) {
      throw new Error('Cello script failed to initialize');
    }

    if (this.initializingPromise) {
      return this.initializingPromise;
    }

    if (!this.celloJwt()) {
      throw new Error('Cello JWT token is not available');
    }

    if (!this.authenticatedUser()) {
      throw new Error('Authenticated user is not available');
    }

    if (!this.scriptLoaded) {
      try {
        await this.loadScript();
      } catch {
        throw new Error('Failed to load Cello script');
      }
    }

    this.bootRetries = 0;

    this.initializingPromise = new Promise((resolve, reject) => {
      if (this.bootInterval) {
        clearInterval(this.bootInterval);
      }

      this.bootInterval = setInterval(() => {
        const celloInstance = (this.window as Window & { Cello: any }).Cello;
        if (celloInstance) {
          clearInterval(this.bootInterval);
          const user = this.authenticatedUser();
          this.window
            .Cello('boot', {
              productId: this.productId,
              token: this.celloJwt(),
              language: user.language || 'en',
              productUserDetails: {
                firstName: user.first_name,
                lastName: user.last_name,
                fullName: user.name,
                email: user.email,
              },
              hideDefaultLauncher: true,
              onOpen: async () => {
                const { ucc, link } = await this.window.Cello('getActiveUcc');
                if (ucc && link) {
                  this.customerIoService.updateUserAttributes({
                    cello_referral_link: String(link),
                    cello_campaign_code: String(ucc),
                  });
                }
              },
            })
            .then(() => {
              this.initialized = true;
              resolve(true);
            })
            .catch(() => {
              this.bootFailed = true;
              reject();
            })
            .finally(() => (this.initializingPromise = null));
        } else if (this.bootRetries >= MAX_RETRIES) {
          clearInterval(this.bootInterval);
          this.initializingPromise = null;
          reject(new Error('Cello script failed to initialize in time'));
        }
        this.bootRetries++;
      }, RETRY_DELAY_MS);
    });

    return this.initializingPromise;
  }

  async openWidget() {
    if (!this.initialized) {
      await this.initialize();
    }
    this.window.Cello('open');
  }

  async getAttributionPayload(): Promise<CelloAttributionPayload> {
    if (!this.attributionScriptLoaded) {
      try {
        await this.loadAttributionScript();
      } catch {
        console.error('Failed to load Cello attribution script');
        return Promise.resolve(this.fallbackUccLookup());
      }
    }

    if (this.attributionPromise) {
      return this.attributionPromise;
    }

    this.attributionPromise = new Promise((resolve) => {
      let retries = 0;
      this.attributionInterval = setInterval(async () => {
        if (this.window.CelloAttribution) {
          const ucc = await this.window.CelloAttribution('getUcc');
          const referrerName = await this.window.CelloAttribution('getReferrerName');
          const campaignConfig = await this.window.CelloAttribution('getCampaignConfig');
          clearInterval(this.attributionInterval);
          resolve({
            ucc,
            referrerName,
            campaignConfig,
          });
        } else if (retries >= MAX_RETRIES) {
          clearInterval(this.attributionInterval);
          console.error('Failed to get Cello attribution payload in time');
          resolve(this.fallbackUccLookup());
        }
        retries++;
      }, RETRY_DELAY_MS);
    });

    return this.attributionPromise;
  }

  private async loadScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      // TODO: Move this URL to environment config SHB-17129
      const src =
        environment.env === 'production'
          ? 'https://assets.cello.so/app/latest/cello.js'
          : 'https://assets.sandbox.cello.so/app/latest/cello.js';
      const script = this.document.createElement('script');
      script.onload = () => {
        this.scriptLoaded = true;
        resolve();
      };
      script.onerror = () => reject();
      script.type = 'module';
      script.src = src;
      this.document.head.appendChild(script);
    });
  }

  private async loadAttributionScript(): Promise<void> {
    if (this.initializingAttributionPromise) {
      return this.initializingAttributionPromise;
    }

    if (this.attributionScriptLoaded) {
      return Promise.resolve();
    }

    this.initializingAttributionPromise = new Promise((resolve, reject) => {
      const url = environment.celloAttributionUrl;
      const script = this.document.createElement('script');
      script.src = url;
      script.async = true;
      script.type = 'module';
      script.onload = () => {
        this.attributionScriptLoaded = true;
        resolve();
      };
      script.onerror = () => reject();
      this.document.head.appendChild(script);
    });

    return this.initializingAttributionPromise;
  }

  // Checks if a ucc parameter is present in the URL and retrieves it
  // If not, checks if cello-referral cookie is set and retrieves its value
  private fallbackUccLookup(): CelloAttributionPayload {
    const uccParam = this.route.snapshot.queryParamMap.get('ucc');
    const referralCookie = this.cookieService.get('cello-referral');
    const ucc: string | null = uccParam || referralCookie || null;

    return {
      ucc,
      referrerName: null,
      campaignConfig: null,
    };
  }
}
